import React, { useEffect, useState } from "react";
import { getRecurrSetupData, getRecurrSetupDataOptions, saveRecurrSetupData, deleteRecurrSetupData } from "../../util/APIUtils";
import { LoadingOverlay } from "../../util/Helpers";
import { Button, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormText } from "reactstrap";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import 'react-bootstrap-table/dist/react-bootstrap-table-all.min.css';
import swal from 'sweetalert';


const tableOptions = {
    sortIndicator: true,
    paginationSize: 3,
    clearSearch: true,
    alwaysShowAllBtns: true,
    withFirstAndLast: true,
    hideSizePerPage: false,
    hidePageListOnlyOnePage: false,
};

const RecurringSetup = () => {
    const [data, setData] = useState([]);
    const [options, setOptions] = useState({ frequencies: [], statuses: [], categories: [], payeeList: [], paymentTypes: [] });

    const [block, setBlock] = useState(false);
    const [showModal, setShowModal] = useState(false);

    // form fields for recurring setup
    const [category, setCategory] = useState('');
    const [amount, setAmount] = useState('');
    const [frequency, setFrequency] = useState('');
    const [dayOfMonth, setDayOfMonth] = useState('1');
    const [startDate, setStartDate] = useState(new Date().toISOString().substring(0, 10));
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState('');
    const [payee, setPayee] = useState('');
    const [paymentType, setPaymentType] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        loadReqData();
        loadOptionsData();
    }, []);

    const loadReqData = () => {
        setBlock(true);
        getRecurrSetupData().then((response) => {
            setData(response);
            setBlock(false);
        });
    };
    const loadOptionsData = () => {
        setBlock(true);
        getRecurrSetupDataOptions().then((response) => {
            // expect response to contain frequencies, statuses, categories, payeeList, paymentTypes
            setOptions(response || { frequencies: [], statuses: [], categories: [], payeeList: [], paymentTypes: [] });
            setBlock(false);
        });
    };
    const saveRecurring = (payload) => {
        setBlock(true);
        return saveRecurrSetupData(payload)
            .then((response) => {
                // show different message for update vs create
                if (payload && payload.id) swal('Updated successfully.'); else swal('Saved successfully.');
                loadReqData();
                setShowModal(false);
                // clear form
                setCategory(''); setAmount(''); setFrequency(''); setDayOfMonth('1'); setStartDate(new Date().toISOString().substring(0, 10)); setDescription(''); setStatus(''); setPayee(''); setPaymentType(''); setEditingId(null);
                return response;
            })
            .catch((err) => {
                const message = (err && err.message) || 'Failed to save record';
                swal('Save error: ' + message);
                throw err;
            })
            .finally(() => setBlock(false));
    };

    const deleteRecurring = (recordId) => {
        setBlock(true);
        deleteRecurrSetupData(recordId)
            .then(() => {
                swal('Record deleted successfully.');
                loadReqData();
            })
            .catch((err) => {
                const message = (err && err.message) || 'Failed to delete record';
                swal('Delete error: ' + message);
            })
            .finally(() => setBlock(false));
    }

    const renderActionButtons = (cell, row, rowIndex) => (
        <span style={{ display: 'flex', gap: 6 }}>
            <Button
                className="btn btn-sm btn-info"
                id={`edit-${rowIndex}`}
                onClick={() => {
                    // populate form and open modal for edit
                    setEditingId(row.id);
                    setCategory(row.categoryId || row.category || '');
                    setAmount(row.amount != null ? String(row.amount) : '');
                    setFrequency(row.frequency || '');
                    setDayOfMonth(row.dayOfMonth != null ? String(row.dayOfMonth) : '1');
                    setStartDate(row.startDate ? row.startDate.substring(0, 10) : new Date().toISOString().substring(0, 10));
                    setDescription(row.description || '');
                    setStatus(row.status || '');
                    setPayee(row.payeeId || row.payee || '');
                    setPaymentType(row.paymentTypeId || row.paymentType || '');
                    setErrors({});
                    setShowModal(true);
                }}
            >
                <i className="fa fa-edit" />
            </Button>
            <Button
                className="btn btn-danger btn-sm"
                id={`delete-${rowIndex}`}
                onClick={() => {
                    swal({
                        title: "Are you sure?",
                        text: "Once deleted, you will not be able to recover this record!",
                        icon: "warning",
                        buttons: true,
                        dangerMode: true,
                    }).then((willDelete) => {
                        if (willDelete) {
                            deleteRecurring(row.id);
                        }
                    });
                }}
            >
                <i className="fa fa-minus-circle" />
            </Button>
        </span>
    );

    const formatDate = (cell) => {
        if (!cell && cell !== 0) return '';
        try {
            if (typeof cell === 'string' && cell.length >= 10) {
                return cell.substring(0, 10);
            }
            const d = new Date(cell);
            if (!isNaN(d.getTime())) return d.toISOString().substring(0, 10);
        } catch (err) { }
        return String(cell);
    };



    return <div>
        {block && <LoadingOverlay />}
        <Card>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Recurring Setup</h5>
                    <div>
                        <Button
                            color="success"
                            className="mr-2"
                            onClick={() => setShowModal(true)}
                            aria-label="Recurring Setup"
                            style={{ borderRadius: 4, paddingLeft: 12, paddingRight: 12 }}
                        >
                            <i className="fa fa-plus mr-2" /> Add Recurring Setup
                        </Button>

                        <Button
                            color="primary"
                            onClick={loadReqData}
                            aria-label="Refresh Documents"
                            style={{ borderRadius: 4, paddingLeft: 12, paddingRight: 12 }}
                        >
                            <i className="fa fa-refresh mr-2" /> Refresh
                        </Button>
                    </div>
                </div>

                {/* recurring setup Modal */}

                <Modal isOpen={showModal} toggle={() => setShowModal(!showModal)} centered modalClassName="doc-modal">
                    <ModalHeader toggle={() => setShowModal(false)}>{editingId ? 'Edit Recurring Setup' : 'Add Recurring Setup'}</ModalHeader>
                    <ModalBody>
                        <Form>
                            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                                <FormGroup style={{ flex: '1 1 220px' }}>
                                    <Label for="category">Category</Label>
                                    <Input id="category" type="select" value={category} onChange={e => setCategory(e.target.value)}>
                                        <option value="">Select</option>
                                        {(options.categories || []).map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.category || c.name} - {c.id}
                                            </option>
                                        ))}
                                    </Input>
                                    {errors.category && <FormText color="danger">{errors.category}</FormText>}
                                </FormGroup>
                                <FormGroup style={{ flex: '1 1 220px' }}>
                                    <Label for="payee">Payee</Label>
                                    <Input id="payee" type="select" value={payee} onChange={e => setPayee(e.target.value)}>
                                        <option value="">Select</option>
                                        {(options.payeeList || []).map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.payeeName} - {p.id}
                                            </option>
                                        ))}
                                    </Input>
                                    {errors.payee && <FormText color="danger">{errors.payee}</FormText>}
                                </FormGroup>
                                <FormGroup style={{ flex: '1 1 220px' }}>
                                    <Label for="paymentType">Payment Type</Label>
                                    <Input id="paymentType" type="select" value={paymentType} onChange={e => setPaymentType(e.target.value)}>
                                        <option value="">Select</option>
                                        {(options.paymentTypes || []).map(pt => (
                                            <option key={pt.id} value={pt.id}>
                                                {pt.configValue} - {pt.id}
                                            </option>
                                        ))}
                                    </Input>
                                    {errors.paymentType && <FormText color="danger">{errors.paymentType}</FormText>}
                                </FormGroup>
                            </div>

                            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                                <FormGroup style={{ flex: '1 1 220px' }}>
                                    <Label for="amount">Amount</Label>
                                    <Input id="amount" type="number" value={amount} onChange={e => setAmount(e.target.value)} />
                                    {errors.amount && <FormText color="danger">{errors.amount}</FormText>}
                                </FormGroup>
                                <FormGroup style={{ flex: '1 1 220px' }}>
                                    <Label for="frequency">Frequency</Label>
                                    <Input id="frequency" type="select" value={frequency} onChange={e => setFrequency(e.target.value)}>
                                        <option value="">Select</option>
                                        {(options.frequencies || []).map(f => <option key={f} value={f}>{f}</option>)}
                                    </Input>
                                    {errors.frequency && <FormText color="danger">{errors.frequency}</FormText>}
                                </FormGroup>
                                <FormGroup style={{ flex: '1 1 220px' }}>
                                    <Label for="dayOfMonth">Day of Month</Label>
                                    <Input id="dayOfMonth" type="number" min={1} max={30} value={dayOfMonth} onChange={e => setDayOfMonth(e.target.value)} />
                                    {errors.dayOfMonth && <FormText color="danger">{errors.dayOfMonth}</FormText>}
                                </FormGroup>
                            </div>

                            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                                <FormGroup style={{ flex: '1 1 220px' }}>
                                    <Label for="startDate">Start Date</Label>
                                    <Input id="startDate" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} readOnly={!!editingId} />
                                    {errors.startDate && <FormText color="danger">{errors.startDate}</FormText>}
                                </FormGroup>
                                <FormGroup style={{ flex: '1 1 220px' }}>
                                    <Label for="status">Status</Label>
                                    <Input id="status" type="select" value={status} onChange={e => setStatus(e.target.value)}>
                                        <option value="">Select</option>
                                        {(options.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
                                    </Input>
                                    {errors.status && <FormText color="danger">{errors.status}</FormText>}
                                </FormGroup>
                            </div>

                            <FormGroup>
                                <Label for="description">Description</Label>
                                <Input id="description" type="textarea" value={description} onChange={e => setDescription(e.target.value)} />
                                {errors.description && <FormText color="danger">{errors.description}</FormText>}
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => { setShowModal(false); setEditingId(null); }}>Cancel</Button>
                        <Button type="button" color="primary" onClick={() => {
                            // validate
                            const e = {};
                            if (!category) e.category = 'Category is required';
                            if (!amount || isNaN(Number(amount))) e.amount = 'Valid amount is required';
                            if (!frequency) e.frequency = 'Frequency is required';
                            const dom = Number(dayOfMonth);
                            if (!dayOfMonth || isNaN(dom) || dom < 1 || dom > 30) e.dayOfMonth = 'Day of month must be 1-30';
                            if (!startDate) e.startDate = 'Start date is required';
                            if (!description) e.description = 'Description is required';
                            if (!status) e.status = 'Status is required';
                            if (!payee) e.payee = 'Payee is required';
                            if (!paymentType) e.paymentType = 'Payment type is required';
                            setErrors(e);
                            if (Object.keys(e).length > 0) return;

                            const payload = {
                                id: editingId,
                                category: (() => {
                                    const cat = (options.categories || []).find(c => c.id == category);
                                    return cat ? `${cat.category || cat.name}-${cat.id}` : category;
                                })(),
                                amount: Number(amount),
                                frequency,
                                dayOfMonth: dom,
                                startDate,
                                description,
                                status,
                                payee: (() => {
                                    const p = (options.payeeList || []).find(p => p.id == payee);
                                    return p ? `${p.payeeName}-${p.id}` : payee;
                                })(),
                                paymentType: (() => {
                                    const pt = (options.paymentTypes || []).find(pt => pt.id == paymentType);
                                    return pt ? `${pt.configValue}-${pt.id}` : paymentType;
                                })()
                            };

                            saveRecurring(payload);
                        }}>{editingId ? 'Update' : 'Save'}</Button>
                    </ModalFooter>
                </Modal>
                <style>{`
                    /* Modern centered modal */
                    .doc-modal .modal-dialog{
                        position: fixed;
                        top: 8%;
                        left: 20%; /* shift modal to the left */
                        transform: translateX(0);
                        max-width: 40%;
                        width: 40%;
                        margin: 0; /* controlled positioning */
                        pointer-events: auto;
                        border-radius: 12px;
                    }
                    .doc-modal .modal-content{
                        border-radius: 12px;
                        box-shadow: 0 12px 40px rgba(0,0,0,0.35);
                        display:flex; flex-direction:column; max-height:80vh; overflow: hidden;
                    }
                    .doc-modal .modal-body{ overflow:auto; padding: 1.25rem; }
                    .doc-modal .modal-header{ border-bottom: none; padding: 1rem 1.25rem; }
                    .doc-modal .modal-footer{ border-top: none; padding: 0.75rem 1.25rem; }
                    @media (max-width: 1200px) {
                        .doc-modal .modal-dialog{ max-width: 75%; width: 75%; left: 12.5%; }
                    }
                    @media (max-width: 768px) {
                        .doc-modal .modal-dialog{ position: relative; top: auto; left: auto; transform: none; max-width: 95%; width: 95%; margin: 1rem auto; }
                    }
                    /* dim background overlay slight tweak */
                    .modal-backdrop.show { opacity: 0.45; }
                `}</style>

                <BootstrapTable
                    data={data}
                    version="4"
                    condensed
                    striped
                    hover
                    pagination
                    search
                    options={options}
                    exportCSV
                    csvFileName="RecurringSetup.csv"
                    trClassName="align-middle"
                    tableStyle={{ tableLayout: "fixed" }}
                >
                    <TableHeaderColumn isKey dataField="id" hidden></TableHeaderColumn>
                    <TableHeaderColumn dataField="category">Category</TableHeaderColumn>
                    <TableHeaderColumn dataField="amount">Amount</TableHeaderColumn>
                    <TableHeaderColumn dataField="frequency">Frequency</TableHeaderColumn>
                    <TableHeaderColumn dataField="dayOfMonth">Day of Month</TableHeaderColumn>
                    <TableHeaderColumn dataField="startDate" dataFormat={formatDate}>Start Date</TableHeaderColumn>
                    <TableHeaderColumn dataField="status">Status</TableHeaderColumn>
                    <TableHeaderColumn dataField="description">Description</TableHeaderColumn>
                    <TableHeaderColumn dataField="payee">Payee</TableHeaderColumn>
                    <TableHeaderColumn dataField="paymentType">Payment Type</TableHeaderColumn>
                    <TableHeaderColumn dataField="actions" dataFormat={renderActionButtons} export={false}>Actions</TableHeaderColumn>

                </BootstrapTable>
            </CardBody>
        </Card>
    </div>;
};
export default RecurringSetup;


// WEBPACK FOOTER //
// ./src/components/CustomComponents/RecurringSetup.js